"use client"

import { DAYS_KEYS, MONTHS_KEYS } from "@/constants/constant"
import { t } from "@/languages/i18n"
import { getCurrencyRate, getHeaderAndFooterTheme, getMenuById } from "@/services/templateServices"
import {
  CurrencyRateData,
  HeaderBannerData,
  HeaderMenuData,
  HeaderSettings,
  HeaderTemplateData,
  UseHomepageReturn,
} from "@/types/types"
import React, { useCallback, useEffect, useRef, useState } from "react"

export const useHeaderTemplate = (templateId: number | undefined, locale: string = "vi") => {
  const [headerData, setHeaderData] = useState<HeaderTemplateData | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const fetchHeaderTemplate = useCallback(async () => {
    if (!templateId) {
      setError(t("Không tìm thấy id mẫu header"))
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const response = await getHeaderAndFooterTheme(templateId, locale)
      setHeaderData(response)
    } catch (err) {
      console.error("Error fetching header template:", err)
      setError(t("Lỗi khi tải mẫu header"))
    } finally {
      setLoading(false)
    }
  }, [templateId, locale])

  useEffect(() => {
    fetchHeaderTemplate()
  }, [])

  return {
    headerData,
    loading,
    error,
    refetch: fetchHeaderTemplate,
  }
}

export const useFooterTemplate = (templateId: number | undefined, locale: string = "vi") => {
  const [footerData, setFooterData] = useState<HeaderTemplateData | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const fetchFooterTemplate = useCallback(async () => {
    if (!templateId) {
      setError(t("Không tìm thấy id mẫu footer"))
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const response = await getHeaderAndFooterTheme(templateId, locale)
      setFooterData(response)
    } catch (err) {
      console.error("Error fetching footer template:", err)
      setError(t("Lỗi khi tải mẫu footer"))
    } finally {
      setLoading(false)
    }
  }, [templateId, locale])

  useEffect(() => {
    fetchFooterTemplate()
  }, [])

  return {
    footerData,
    loading,
    error,
    refetch: fetchFooterTemplate,
  }
}

export const useHomepage = (locale: string = "vi"): UseHomepageReturn => {
  const [headerSettings, setHeaderSettings] = useState<HeaderSettings | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const formatCurrentDate = useCallback((): string => {
    const now = new Date()
    const day = now.getDate()
    const month = now.getMonth()
    const year = now.getFullYear()
    const dayOfWeek = now.getDay()

    const dayName = t(DAYS_KEYS[dayOfWeek])
    const monthName = t(MONTHS_KEYS[month])
    const formattedDay = String(day).padStart(2, "0")

    if (locale === "en") {
      return `${dayName}, ${monthName} ${formattedDay}, ${year}`
    } else if (locale === "cs") {
      return `${dayName}, ${formattedDay} ${monthName} ${year}`
    } else {
      return `${dayName}, ${formattedDay} ${monthName}, ${year}`
    }
  }, [locale])

  const fetchHeaderData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const menuId = 2

      const [menuData, currencyRateData] = await Promise.all([getMenuById(menuId), getCurrencyRate()])

      const bannerData: HeaderBannerData = {
        imageUrl: "/images/banner-header.jpg",
        altText: "Tamda Media Banner",
      }

      const currentDate = formatCurrentDate()

      setHeaderSettings({
        banner: bannerData,
        menuData: menuData as HeaderMenuData | null,
        currencyRate: currencyRateData as CurrencyRateData | null,
        currentDate,
      })

      setLoading(false)
    } catch (err) {
      console.error("Error fetching header data:", err)
      setError(t("Lỗi khi tải dữ liệu header"))
      setLoading(false)
    }
  }, [locale, formatCurrentDate])

  useEffect(() => {
    fetchHeaderData()
  }, [])

  return {
    headerSettings,
    loading,
    error,
  }
}

export const useStickyHeaderList = (headerListRef: React.RefObject<HTMLDivElement | null>) => {
  const [isSticky, setIsSticky] = useState(false)
  const headerListInitialTop = useRef<number>(0)
  const isStickyRef = useRef<boolean>(false)

  useEffect(() => {
    isStickyRef.current = isSticky
  }, [isSticky])

  useEffect(() => {
    const updateInitialPosition = () => {
      if (headerListRef.current && !isStickyRef.current) {
        const rect = headerListRef.current.getBoundingClientRect()
        headerListInitialTop.current = rect.top + window.scrollY
      }
    }

    const scheduleInitialize = () => {
      requestAnimationFrame(() => {
        updateInitialPosition()
      })
    }

    scheduleInitialize()

    const handleScroll = () => {
      if (!headerListRef.current) return

      updateInitialPosition()

      const currentScrollY = window.scrollY
      const shouldBeSticky = currentScrollY >= headerListInitialTop.current && headerListInitialTop.current > 0

      if (shouldBeSticky !== isStickyRef.current) {
        setIsSticky(shouldBeSticky)
      }
    }

    const handleResize = () => {
      setIsSticky(false)
      isStickyRef.current = false
      headerListInitialTop.current = 0
      requestAnimationFrame(() => {
        updateInitialPosition()
        handleScroll()
      })
    }

    handleScroll()

    window.addEventListener("scroll", handleScroll, { passive: true })
    window.addEventListener("resize", handleResize, { passive: true })

    return () => {
      window.removeEventListener("scroll", handleScroll)
      window.removeEventListener("resize", handleResize)
    }
  }, [headerListRef])

  return isSticky
}

export const useBannerResize = (imageUrl: string) => {
  const [containerHeight, setContainerHeight] = useState<number | null>(null)
  const [containerWidth, setContainerWidth] = useState<number>(1128)

  useEffect(() => {
    let isMounted = true
    let img: HTMLImageElement | null = null
    let updateHeight: (() => void) | null = null

    const updateWidth = () => {
      if (isMounted) {
        setContainerWidth(window.innerWidth < 640 ? 343 : 1128)
      }
    }

    updateWidth()
    window.addEventListener("resize", updateWidth)

    img = new window.Image()

    img.onload = () => {
      if (!isMounted || !img) return

      const aspectRatio = img.naturalHeight / img.naturalWidth

      updateHeight = () => {
        if (!isMounted) return
        const width = window.innerWidth < 640 ? 343 : 1128
        const calculatedHeight = width * aspectRatio
        setContainerHeight(calculatedHeight)
      }

      updateHeight()
      window.addEventListener("resize", updateHeight)
    }

    img.onerror = () => {
      if (isMounted) {
        setContainerHeight(null)
      }
    }

    img.src = imageUrl

    return () => {
      isMounted = false
      window.removeEventListener("resize", updateWidth)
      if (updateHeight) {
        window.removeEventListener("resize", updateHeight)
      }
      if (img) {
        img.onload = null
        img.onerror = null
        img.src = ""
        img = null
      }
    }
  }, [imageUrl])

  return { containerWidth, containerHeight }
}

export const useStickySidebar = (sidebarRef: React.RefObject<HTMLDivElement | null>) => {
  const [isSticky, setIsSticky] = useState(false)
  const sidebarInitialTop = useRef<number>(0)
  const isStickyRef = useRef<boolean>(false)

  useEffect(() => {
    isStickyRef.current = isSticky
  }, [isSticky])

  useEffect(() => {
    const updateInitialPosition = () => {
      if (sidebarRef.current && !isStickyRef.current) {
        const rect = sidebarRef.current.getBoundingClientRect()
        sidebarInitialTop.current = rect.top + window.scrollY
      }
    }

    const scheduleInitialize = () => {
      requestAnimationFrame(() => {
        updateInitialPosition()
      })
    }

    scheduleInitialize()

    const handleScroll = () => {
      if (!sidebarRef.current) return

      updateInitialPosition()

      const currentScrollY = window.scrollY
      const shouldBeSticky = currentScrollY + 40 >= sidebarInitialTop.current && sidebarInitialTop.current > 0

      if (shouldBeSticky !== isStickyRef.current) {
        setIsSticky(shouldBeSticky)
      }
    }

    const handleResize = () => {
      updateInitialPosition()
      handleScroll()
    }

    handleScroll()

    window.addEventListener("scroll", handleScroll, { passive: true })
    window.addEventListener("resize", handleResize, { passive: true })

    return () => {
      window.removeEventListener("scroll", handleScroll)
      window.removeEventListener("resize", handleResize)
    }
  }, [sidebarRef])

  return isSticky
}
